import {
  ArrowBackIos,
  ArrowForwardIos,
  History as HistoryIcon,
} from '@mui/icons-material'
import {
  Avatar,
  Button,
  Card,
  CardMedia,
  CircularProgress,
  Container,
  Divider,
  Drawer,
  Fab,
  Grid2,
  IconButton,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material'
import { useQuery } from '@tanstack/react-query'
import Lottie from 'lottie-react'
import moment from 'moment'
import { useTranslation } from 'react-i18next'
import { Link as RouterLink } from 'react-router-dom'

import logo from '~/assets/header_logo.png'
import animationData from '~/assets/ilustracion_loading.json'
import ActivityFeed from '~/components/ActivityFeed'
import MainLayout from '~/components/layouts/MainLayout'
import { MUIWrapperContext } from '~/components/providers/MuiWrapperProvider'
import i18n from '~/i18n'
import { queries } from '~/queries'
import { useAppSelector } from '~/redux/hooks'
import { getDateFormatByLocale } from '~/utils/date-utils'
import { type PlatformLanguage } from '~/utils/types/i18n'

import noArticlesImg from '../../assets/empty_state_ilu.svg'

export default function Homepage() {
  const { t, i18n } = useTranslation()
  const { isLtR } = useContext(MUIWrapperContext)

  const [showFeed, setShowFeed] = useState(false)

  const user = useAppSelector(state => {
    if (!state.user) throw new Error('No user')
    return state.user
  })

  const dashboardActivityFeed = useQuery({
    ...queries.announcements.activityFeed(i18n.language as PlatformLanguage),
  })

  const listAnnouncementsQuery = useQuery({
    ...queries.announcements.listPaginable({
      page: 0,
      pageSize: 6,
      filter: '',
      from: '',
      to: '',
      language: i18n.language as PlatformLanguage,
    }),

    select: res => ({
      data: res.data.data.listPaginableAnnouncements.content,
      total: res.data.data.listPaginableAnnouncements.totalElements,
    }),
  })

  const isLoading = listAnnouncementsQuery.isLoading

  return (
    <MainLayout>
      <Container maxWidth="xl">
        <Stack direction="row" justifyContent="flex-start" pt={2}>
          <Tooltip title={t('views.dashboard.latestActivity')}>
            <Fab
              size="small"
              color="primary"
              variant="extended"
              onClick={() => {
                setShowFeed(!showFeed)
              }}
            >
              <HistoryIcon />
              {isLtR ? <ArrowForwardIos /> : <ArrowBackIos />}
            </Fab>
          </Tooltip>
        </Stack>
      </Container>

      <Drawer
        anchor="left"
        open={showFeed}
        onClose={() => {
          setShowFeed(false)
        }}
        slotProps={{
          paper: {
            sx: {
              overflow: 'hidden',
              borderTopRightRadius: { xs: 0, md: 50 },
              bgcolor: theme => theme.palette.background.default,
            },
          },
        }}
      >
        <Stack
          px={2}
          pt={4}
          sx={{
            height: '100vh',
            width: { xs: '100vw', md: '75vw', lg: '33vw' },
          }}
        >
          <Stack direction="row" justifyContent="space-between">
            <Typography variant="h4">
              {t('views.dashboard.latestActivity')}
            </Typography>
            <IconButton
              color="primary"
              sx={{ borderRadius: 5 }}
              onClick={() => {
                setShowFeed(false)
              }}
            >
              {isLtR ? <ArrowBackIos /> : <ArrowForwardIos />}
              <HistoryIcon />
            </IconButton>
          </Stack>
          <Divider sx={{ my: 2 }} />
          {dashboardActivityFeed.isLoading && (
            <Stack height="100%" alignItems="center" justifyContent="center">
              <CircularProgress />
            </Stack>
          )}
          {dashboardActivityFeed.isSuccess &&
            dashboardActivityFeed.data?.data && (
              <ActivityFeed
                width="100%"
                height="100%"
                data={dashboardActivityFeed.data.data.data.recentActivity}
              />
            )}
        </Stack>
      </Drawer>

      <Container maxWidth="xl" sx={{ p: 4 }}>
        <Stack alignItems="center" spacing={2}>
          <Avatar
            src={logo}
            sx={{ width: 56, height: 56, mb: 1, bgcolor: 'white' }}
          />
          <Typography variant="h4" fontWeight="bold" align="center">
            {t('views.survey.welcome')} {user.name}!
          </Typography>
          <Stack
            gap={4}
            direction={{ xs: 'column', md: 'row' }}
            alignItems="center"
            justifyContent="center"
            width="100%"
          >
            <Lottie
              animationData={animationData}
              loop={true}
              style={{ width: '70vw', maxWidth: 500, height: 'auto' }}
            />

            <Stack spacing={2} alignItems="flex-start" sx={{ maxWidth: 400 }}>
              <Typography variant="h6">
                {t('views.survey.welcomeDetail')}
              </Typography>
            </Stack>
          </Stack>
        </Stack>
      </Container>

      <Container maxWidth="xl" sx={{ mb: 6 }}>
        <Typography
          variant="h5"
          fontWeight="bold"
          sx={{ mb: 3, textAlign: 'center' }}
        >
          {t('homepage.whatsNew')}
        </Typography>
        {isLoading && (
          <Grid2 container spacing={3}>
            {Array.from({ length: 6 }).map((_, i) => (
              <Grid2 key={i} size={{ xs: 12, md: 4 }}>
                <Skeleton
                  variant="rectangular"
                  height={180}
                  sx={{ borderRadius: 2, mb: 2 }}
                />
              </Grid2>
            ))}
          </Grid2>
        )}

        {!isLoading &&
          (listAnnouncementsQuery.data?.data.length === 0 ? (
            <Stack
              gap={2}
              flexDirection="column"
              alignItems="center"
              justifyContent="center"
              sx={{ height: 300, mt: 6 }}
            >
              <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
                {t('homepage.noAnnouncements')}
              </Typography>
              <Typography variant="body2" color="text.disabled">
                {t('homepage.noAnnouncementsDetail')}
                <CardMedia
                  component="img"
                  src={noArticlesImg}
                  sx={{ width: '100%', maxWidth: 300, height: 'auto' }}
                />
              </Typography>
            </Stack>
          ) : (
            <>
              <Grid2 container spacing={3}>
                {listAnnouncementsQuery.data?.data.map(item => (
                  <Grid2 key={item.id} size={{ xs: 12, md: 4 }}>
                    <AnnouncementCard
                      date={item.announcementDate}
                      title={item.title}
                      description={item.content}
                      link="/announcements"
                    />
                  </Grid2>
                ))}
              </Grid2>
              <Stack alignItems="center" sx={{ mt: 6 }}>
                <Button
                  component={RouterLink}
                  to="/announcements"
                  variant="contained"
                  sx={{ backgroundColor: theme => theme.palette.primary.main }}
                >
                  {t('views.support.viewmore')}
                </Button>
              </Stack>
            </>
          ))}
      </Container>
    </MainLayout>
  )
}

interface AnnouncementCardProps {
  date: string
  title: string
  description: string
  link: string
}

function AnnouncementCard({
  date,
  title,
  description,
  link,
}: AnnouncementCardProps) {
  const { t } = useTranslation()
  const dateFormat = getDateFormatByLocale(i18n.language as PlatformLanguage)

  return (
    <Card
      variant="outlined"
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        p: 2,
      }}
    >
      <Typography variant="subtitle2" color="success.main" sx={{ mb: 1 }}>
        {date ? moment(new Date(date)).format(dateFormat) : ''}
      </Typography>
      <Typography variant="body1" fontWeight="bold" sx={{ mb: 1 }}>
        {title}
      </Typography>
      <Typography variant="body2" sx={{ flexGrow: 1, mb: 2 }}>
        {description}
      </Typography>
      <Button
        variant="text"
        color="success"
        size="small"
        sx={{ alignSelf: 'flex-start', fontWeight: 'bold' }}
        href={link}
      >
        {t('views.support.viewmore')}
      </Button>
    </Card>
  )
}
